import {
    ChangeDetectionStrategy,
    Component,
    Inject,
    OnInit,
} from '@angular/core';
import {BehaviorSubject, Observable} from 'rxjs';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {Toast} from '@common/core/ui/toast.service';
import {Messages} from '../../../messages.enum';
import {LinkGroup} from '../../../shared/link/link-group';
import {LinkGroupService} from '../../../shared/link/link-group.service';
import {FormControl, FormGroup} from '@angular/forms';
import {finalize} from 'rxjs/operators';
import {BackendErrorResponse} from '@common/core/types/backend-error-response';
import {randomString} from '@common/core/utils/random-string';
import {ValueLists} from '@common/core/services/value-lists.service';
import {CustomDomain} from '@common/custom-domain/custom-domain';
import {Settings} from '@common/core/config/settings.service';
import {BiolinksService} from '../../biolinks/biolinks.service';
import {BackendResponse} from '@common/core/types/backend-response';
import {Biolink} from '../../biolinks/biolink';

export interface CrupdateLinkGroupModalData {
    linkGroup: LinkGroup;
    type?: 'group' | 'biolink';
}

@Component({
    selector: 'crupdate-link-group-modal',
    templateUrl: './crupdate-link-group-modal.component.html',
    styleUrls: ['./crupdate-link-group-modal.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CrupdateLinkGroupModalComponent implements OnInit {
    loading$ = new BehaviorSubject(false);
    updating$ = new BehaviorSubject(false);
    errors$ = new BehaviorSubject<Partial<LinkGroup>>({});
    form = new FormGroup({
        name: new FormControl(),
        active: new FormControl(true),
        description: new FormControl(''),
        hash: new FormControl(randomString(6)),
        rotator: new FormControl(false),
        domain_id: new FormControl(null),
    });
    slugBaseUri$ = new BehaviorSubject<string>(null);
    domains$ = new BehaviorSubject<CustomDomain[]>([]);

    get resourceName(): string {
        return this.data.type === 'biolink' ? 'biolink' : 'link group';
    }

    constructor(
        private dialogRef: MatDialogRef<CrupdateLinkGroupModalComponent>,
        @Inject(MAT_DIALOG_DATA) public data: CrupdateLinkGroupModalData,
        private groups: LinkGroupService,
        private biolinks: BiolinksService,
        private toast: Toast,
        private valueLists: ValueLists,
        private settings: Settings
    ) {}

    ngOnInit() {
        this.updating$.next(!!this.data.linkGroup);
        if (this.data.linkGroup) {
            this.form.patchValue(this.data.linkGroup);
        }

        this.valueLists.get(['domains']).subscribe(response => {
            this.domains$.next(response.domains);
            this.setSlugBaseUri(this.form.value.domain_id);
        });

        this.form.get('domain_id').valueChanges.subscribe(domainId => {
            this.setSlugBaseUri(domainId);
        });
    }

    confirm() {
        this.loading$.next(true);

        const request: BackendResponse<{biolink?: Biolink; linkGroup?: LinkGroup}> =
            this.updating$.value
                ? this.api().update(this.data.linkGroup.id, this.form.value)
                : this.api().create(this.form.value);

        request.pipe(finalize(() => this.loading$.next(false))).subscribe(
            response => {
                this.toast.open(
                    this.updating$.value
                        ? `${this.resourceName} updated`
                        : `${this.resourceName} created`
                );
                this.close(response.linkGroup || response.biolink);
            },
            (errResponse: BackendErrorResponse) =>
                this.errors$.next(errResponse.errors)
        );
    }

    close(group?: LinkGroup) {
        this.dialogRef.close(group);
    }

    private api(): BiolinksService | LinkGroupService {
        return this.data.type === 'biolink' ? this.biolinks : this.groups;
    }

    private setSlugBaseUri(domainId: number) {
        if (domainId) {
            this.slugBaseUri$.next(
                this.domains$.value.find(d => d.id === domainId).host
            );
        } else {
            this.slugBaseUri$.next(this.settings.getBaseUrl());
        }
    }
}
